<template>
  <teleport to="body">
    <div
      v-if="show"
      class="modal-overlay"
      @click.self="handleClose"
      @keydown.esc="handleClose"
    >
      <div
        class="modal-window"
        ref="seModal"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-help-title"
        aria-describedby="cp-help-desc"
      >
        <div class="modal-shell">

          <!-- ============ HEADER ============ -->
          <div class="modal-head">
            <div class="head-icon" aria-hidden="true">
              <i class="bi bi-people-fill"></i>
            </div>
            <div class="head-text">
              <div class="head-eyebrow">{{ $t('gaugeHelp.cp.eyebrow') }}</div>
              <h2 class="head-title" id="cp-help-title">
                {{ $t('gaugeHelp.cp.title') }}
              </h2>
            </div>
            <button
              class="head-close"
              type="button"
              @click="handleClose"
              :aria-label="$t('gaugeHelp.common.close')"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- ============ BODY ============ -->
          <div class="modal-body" id="cp-help-desc">

            <!-- 1. WHAT IT SHOWS -->
            <section class="card-row tldr">
              <div class="card-row-label">{{ $t('gaugeHelp.common.whatItShows') }}</div>
              <i18n-t keypath="gaugeHelp.cp.what" tag="p" class="card-row-body" scope="global">
                <template #used><strong>{{ $t('gaugeHelp.cp.used') }}</strong></template>
                <template #hundred><strong>100%</strong></template>
                <template #spare><strong>{{ $t('gaugeHelp.cp.spare') }}</strong></template>
                <template #serve><strong>{{ $t('gaugeHelp.cp.serve') }}</strong></template>
              </i18n-t>
            </section>

            <!-- 2. HOW IT'S CALCULATED -->
            <section class="card-row">
              <div class="card-row-label">{{ $t('gaugeHelp.common.howCalculated') }}</div>

              <div class="formula-card">
                <div class="formula-line">
                  <span class="formula-term">{{ $t('gaugeHelp.cp.load') }}</span>
                  <span class="formula-eq">=</span>
                  <span class="formula-expr">
                    <i18n-t keypath="gaugeHelp.cp.loadExpr" scope="global">
                      <template #cust><em>{{ $t('gaugeHelp.cp.cust') }}</em></template>
                      <template #lanes><em>{{ $t('gaugeHelp.cp.lanes') }}</em></template>
                    </i18n-t>
                    <span class="formula-note">{{ $t('gaugeHelp.cp.loadNote') }}</span>
                  </span>
                </div>

                <div class="formula-divider"></div>

                <div class="formula-line formula-result">
                  <span class="formula-term">{{ $t('gaugeHelp.cp.result') }}</span>
                  <span class="formula-eq">=</span>
                  <span class="formula-expr">
                    <strong>{{ $t('gaugeHelp.cp.resultExpr') }}</strong>
                    <span class="formula-note">{{ $t('gaugeHelp.cp.resultNote') }}</span>
                  </span>
                </div>
              </div>

              <p class="plain-help">
                {{ $t('gaugeHelp.cp.examples') }}
              </p>

              <div class="example-card">
                <div class="example-row">
                  <span class="ex-tag ex-balanced">{{ $t('gaugeHelp.cp.exBalancedTag') }}</span>
                  <i18n-t keypath="gaugeHelp.cp.exBalanced" tag="span" class="ex-body" scope="global">
                    <template #pct><strong>100%</strong></template>
                  </i18n-t>
                </div>
                <div class="example-row">
                  <span class="ex-tag ex-overstaffed">{{ $t('gaugeHelp.cp.exOverTag') }}</span>
                  <i18n-t keypath="gaugeHelp.cp.exOver" tag="span" class="ex-body" scope="global">
                    <template #pct><strong>60%</strong></template>
                  </i18n-t>
                </div>
              </div>
            </section>

            <!-- 3. COST BASIS (instead of "sector benchmark" — there's no
                 sector "balanced ratio" benchmark; balance is intrinsic
                 to ratio = 1.0). Shows the €/hr that drives the two
                 €-loss figures on the gauge. -->
            <section class="card-row">
              <div class="card-row-label">{{ $t('gaugeHelp.cp.costLabel') }}</div>

              <div class="bench-card">
                <div class="bench-row">
                  <div class="bench-cell">
                    <div class="bench-val">{{ rateText }} <span class="bench-unit">{{ $t('gaugeHelp.cp.perHour') }}</span></div>
                    <div class="bench-label">{{ rateLabel }}</div>
                    <div class="bench-hint">{{ rateHint }}</div>
                  </div>
                </div>

                <div class="bench-source">
                  <span
                    class="confidence-dot"
                    :class="`conf-${rateConfidence}`"
                    aria-hidden="true"
                  ></span>
                  <span class="bench-source-label">{{ rateSourceLabel }}:</span>
                  {{ rateSource }}
                </div>
              </div>

              <i18n-t keypath="gaugeHelp.cp.plainCost" tag="p" class="plain-help" scope="global">
                <template #fig><strong>{{ $t('gaugeHelp.cp.fig') }}</strong></template>
              </i18n-t>
            </section>

            <!-- 4. HOW TO READ YOUR SCORE -->
            <section class="card-row">
              <div class="card-row-label">{{ $t('gaugeHelp.common.howToRead') }}</div>

              <div class="range-card">
                <div class="range-row range-red">
                  <div class="range-band">&lt; 60%</div>
                  <div class="range-body">
                    <strong>{{ $t('gaugeHelp.cp.redTitle') }}</strong>
                    {{ $t('gaugeHelp.cp.redBody') }}
                  </div>
                </div>
                <div class="range-row range-orange">
                  <div class="range-band">60 – 90%</div>
                  <div class="range-body">
                    <strong>{{ $t('gaugeHelp.cp.orangeTitle') }}</strong>
                    {{ $t('gaugeHelp.cp.orangeBody') }}
                  </div>
                </div>
                <div class="range-row range-green">
                  <div class="range-band">90% +</div>
                  <div class="range-body">
                    <strong>{{ $t('gaugeHelp.cp.greenTitle') }}</strong>
                    {{ $t('gaugeHelp.cp.greenBody') }}
                  </div>
                </div>
              </div>
            </section>

            <!-- 5. METHODOLOGY (collapsible) -->
            <section class="card-row methodology">
              <button
                class="methodology-toggle"
                type="button"
                @click="methodologyOpen = !methodologyOpen"
                :aria-expanded="methodologyOpen"
              >
                <i
                  class="bi"
                  :class="methodologyOpen ? 'bi-chevron-down' : 'bi-chevron-right'"
                ></i>
                {{ $t('gaugeHelp.cp.methToggle') }}
              </button>

              <div v-if="methodologyOpen" class="methodology-body">
                <i18n-t keypath="gaugeHelp.cp.methP1" tag="p" scope="global">
                  <template #loaded><strong>{{ $t('gaugeHelp.cp.loaded') }}</strong></template>
                </i18n-t>

                <p>
                  {{ $t('gaugeHelp.cp.methP2') }}
                </p>

                <div class="meth-pillar">
                  <span class="meth-num">1</span>
                  <div>
                    <strong>{{ $t('gaugeHelp.cp.m1Title') }}</strong>
                    <i18n-t keypath="gaugeHelp.cp.m1Body" scope="global">
                      <template #path><em>{{ $t('gaugeHelp.cp.m1Path') }}</em></template>
                    </i18n-t>
                  </div>
                </div>

                <div class="meth-pillar">
                  <span class="meth-num">2</span>
                  <div>
                    <strong>{{ $t('gaugeHelp.cp.m2Title') }}</strong>
                    <i18n-t keypath="gaugeHelp.cp.m2Body" scope="global">
                      <template #pc><em>{{ $t('gaugeHelp.cp.pc') }}</em></template>
                    </i18n-t>
                  </div>
                </div>

                <i18n-t keypath="gaugeHelp.cp.methFine" tag="p" class="meth-fine" scope="global">
                  <template #belgium><strong>{{ $t('gaugeHelp.cp.belgium') }}</strong></template>
                  <template #doc><em>{{ $t('gaugeHelp.common.methodDoc') }}</em></template>
                </i18n-t>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatEuro } from '@/i18n/format'
import { CAPACITY_VALUE_SETTINGS } from '@/prioritization/settings'

const props = defineProps({
  show: { type: Boolean, default: false },
  segmentKey: { type: String, default: 'default' },
  // Optional company-level override for the staff cost. When set,
  // the modal shows that rate as the active value and labels it as
  // "your configured rate"; otherwise it falls back to the sector
  // default and labels it as such.
  companyStaffCost: { type: Number, default: null },
})
const emit = defineEmits(['close'])
const { t } = useI18n()

// Sector names on screen: gaugeHelp.sectors.*
const SECTOR_KEYS = ['default', 'grocery', 'diy', 'furniture', 'fashion', 'discount', 'pharmacy', 'specialty', 'large_format']
const sectorName = (key) => t(`gaugeHelp.sectors.${SECTOR_KEYS.includes(key) ? key : 'default'}`)

// Resolved effective rate — override wins when valid, otherwise
// fall back to sector default. Mirrors the resolution logic in the
// gauge so the modal and the gauge always agree.
const sectorCfg = computed(
  () => CAPACITY_VALUE_SETTINGS[props.segmentKey] || CAPACITY_VALUE_SETTINGS.default
)

const overrideRate = computed(() => {
  const v = Number(props.companyStaffCost)
  return (Number.isFinite(v) && v > 0) ? v : null
})

const effectiveRate = computed(() => {
  if (overrideRate.value !== null) return overrideRate.value
  return Number(sectorCfg.value?.staff_cost_per_hour) || 0
})

// English shows "€20" as before; Dutch "€ 20" / "€ 19,50".
const rateText = computed(() => {
  const v = effectiveRate.value
  if (currentLanguage() === 'en') return `€${v}`
  return formatEuro(v, { decimals: Number.isInteger(v) ? 0 : 2 })
})

const rateLabel = computed(() => {
  return overrideRate.value !== null
    ? t('gaugeHelp.cp.rateOverride')
    : t('gaugeHelp.cp.rateSector', { sector: sectorName(props.segmentKey) })
})

const rateHint = computed(() => {
  return overrideRate.value !== null
    ? t('gaugeHelp.cp.hintOverride')
    : t('gaugeHelp.cp.hintSector')
})

const rateConfidence = computed(() => {
  // Override = direct, trust-the-admin (treat as cross-vendor-equivalent).
  // Sector default = structured synthesis (Belgian joint-committee tables).
  return overrideRate.value !== null ? 'cvt' : 'srs'
})

const rateSourceLabel = computed(() => {
  return overrideRate.value !== null ? t('gaugeHelp.cp.srcLabelOverride') : t('gaugeHelp.common.conf.srsShort')
})

const rateSource = computed(() => {
  if (overrideRate.value !== null) {
    return t('gaugeHelp.cp.srcOverride')
  }
  return t('gaugeHelp.cp.srcSector')
})

const methodologyOpen = ref(false)
const seModal = ref(null)

function handleClose() {
  emit('close')
}

watch(
  () => props.show,
  async (open) => {
    if (open) {
      document.body.classList.add('modal-open')
      await nextTick()
      seModal.value?.focus?.()
    } else {
      document.body.classList.remove('modal-open')
      methodologyOpen.value = false
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  document.body.classList.remove('modal-open')
})
</script>

<style scoped>
/* ============================================================
   OVERLAY + WINDOW
============================================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 26, 38, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;   /* above the nav sidebar (1100) so the modal isn't covered */
  padding: 24px;
}

.modal-window {
  background: #fff;
  border-radius: 16px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(13, 38, 76, 0.18);
  outline: none;
}

.modal-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* ============================================================
   HEADER
============================================================ */
.modal-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef2f8;
  background: linear-gradient(180deg, #f6f9fd 0%, #fff 100%);
}

.head-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  /* Capacity-accent — deep blue, matches the gauge card's left border. */
  background: var(--stora-accent-deep);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex: 0 0 auto;
}

.head-text { flex: 1 1 auto; min-width: 0; }

.head-eyebrow {
  font-size: 11px;
  font-weight: 600;
  color: #6b7a87;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.head-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a2a36;
  margin: 0;
  line-height: 1.25;
}

.head-close {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e6edf3;
  background: #fff;
  color: #6b7a87;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.head-close:hover {
  background: #f6f9fd;
  color: #1a2a36;
}

/* ============================================================
   BODY
============================================================ */
.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ============================================================
   CARD ROW
============================================================ */
.card-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card-row-label {
  font-size: 11px;
  font-weight: 700;
  color: #4F7EB3;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.card-row-body {
  margin: 0;
  font-size: 14px;
  color: #2b3a46;
  line-height: 1.55;
}
.card-row.tldr .card-row-body { font-size: 15px; }

/* ============================================================
   FORMULA CARD
============================================================ */
.formula-card {
  background: #f6f9fd;
  border: 1px solid #e3ecf7;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.formula-line {
  display: grid;
  grid-template-columns: 110px 18px 1fr;
  gap: 10px;
  align-items: start;
  font-size: 13.5px;
  color: #2b3a46;
  line-height: 1.5;
}

.formula-term { font-weight: 700; color: #1a2a36; }
.formula-eq   { font-weight: 700; color: #6b7a87; text-align: center; }
.formula-expr em {
  font-style: italic;
  color: #B07916;
  font-weight: 600;
}
.formula-note {
  display: block;
  font-size: 11.5px;
  color: #7a8895;
  font-style: italic;
  margin-top: 2px;
}

.formula-divider { height: 1px; background: #d8e3f0; margin: 4px 0; }
.formula-result { font-size: 14.5px; }
.formula-result .formula-expr strong { color: var(--stora-accent-deep); }

.plain-help {
  margin: 0;
  font-size: 13px;
  color: #6b7a87;
  line-height: 1.55;
  font-style: italic;
}

/* ============================================================
   EXAMPLE CARD (capacity-specific — 3 ratio examples)
============================================================ */
.example-card {
  background: #fff;
  border: 1px solid #e3ecf7;
  border-radius: 12px;
  overflow: hidden;
}

.example-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  font-size: 13px;
  color: #2b3a46;
  line-height: 1.4;
  border-bottom: 1px solid #eef2f8;
}
.example-row:last-child { border-bottom: none; }

.ex-tag {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 10px;
  border-radius: 999px;
  text-align: center;
}
.ex-balanced    { background: #E8F7F0; color: #2C6F58; }
.ex-overstaffed { background: #FFF4E5; color: #A56B1F; }
.ex-overloaded  { background: #FDE7EA; color: #B44A55; }

.ex-body strong { color: #1a2a36; font-variant-numeric: tabular-nums; }

/* ============================================================
   BENCHMARK / COST-BASIS CARD — Capacity orange tone
============================================================ */
.bench-card {
  background: linear-gradient(180deg, #fbf3df 0%, #fdf9ed 100%);
  border: 1px solid #ebd5a3;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.bench-row {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 8px;
}

.bench-cell {
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2px;
}

.bench-val {
  font-size: 28px;
  font-weight: 800;
  color: #B07916;
  letter-spacing: -0.5px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.bench-unit {
  font-size: 14px;
  font-weight: 600;
  color: #6b7a87;
  margin-left: 2px;
}

.bench-label {
  font-size: 12px;
  font-weight: 600;
  color: #7a560a;
  margin-top: 4px;
}

.bench-hint {
  font-size: 11px;
  color: #6b7a87;
  font-style: italic;
  line-height: 1.3;
  max-width: 260px;
}

.bench-source {
  font-size: 12px;
  color: #7a560a;
  display: flex;
  gap: 6px;
  align-items: baseline;
  padding-top: 10px;
  border-top: 1px solid #ebd5a3;
  line-height: 1.5;
}
.bench-source-label { font-weight: 700; }

/* ============================================================
   CONFIDENCE DOT
============================================================ */
.confidence-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: 0 0 auto;
  position: relative;
  top: 1px;
}
.conf-pr  { background: #36910d; }
.conf-cvt { background: #4F7EB3; }
.conf-srs { background: #D3A741; }

/* ============================================================
   RANGE CARD
============================================================ */
.range-card {
  background: #fff;
  border: 1px solid #e3ecf7;
  border-radius: 12px;
  overflow: hidden;
}

.range-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  font-size: 13.5px;
  color: #2b3a46;
  line-height: 1.5;
  border-left: 4px solid transparent;
  border-bottom: 1px solid #eef2f8;
}
.range-row:last-child { border-bottom: none; }

.range-band {
  font-weight: 700;
  color: #1a2a36;
  font-variant-numeric: tabular-nums;
}
.range-body strong { color: #1a2a36; }

.range-red    { border-left-color: #B44A55; background: #fdf5f5; }
.range-orange { border-left-color: #E9A44A; background: #fefaf2; }
.range-green  { border-left-color: #2C6F58; background: #f4faf3; }

/* ============================================================
   METHODOLOGY (collapsible) — for capacity, uses numbered steps
   (1: company override, 2: sector default) instead of the PR/CVT/SRS
   pillars used in the other two modals.
============================================================ */
.methodology { margin-top: 6px; }

.methodology-toggle {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #B07916;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  text-align: left;
}
.methodology-toggle:hover { text-decoration: underline; }
.methodology-toggle .bi { font-size: 12px; }

.methodology-body {
  margin-top: 12px;
  padding: 16px 18px;
  background: #f6f9fd;
  border: 1px solid #e3ecf7;
  border-radius: 12px;
  font-size: 13px;
  color: #2b3a46;
  line-height: 1.55;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.methodology-body p { margin: 0; }
.methodology-body strong { color: #1a2a36; }

.meth-pillar {
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 8px;
  align-items: start;
  font-size: 12.5px;
}

.meth-num {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--stora-accent-deep);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.meth-fine {
  font-size: 12px;
  color: #6b7a87;
  font-style: italic;
  border-top: 1px solid #d8e3f0;
  padding-top: 10px;
  margin-top: 4px;
}

/* ============================================================
   GLOBAL: lock body scroll while modal is open
============================================================ */
:global(body.modal-open) { overflow: hidden; }

/* ============================================================
   RESPONSIVE
============================================================ */
@media (max-width: 540px) {
  .formula-line { grid-template-columns: 1fr; gap: 4px; }
  .formula-eq { display: none; }
  .range-row { grid-template-columns: 1fr; gap: 4px; }
  .example-row { grid-template-columns: 1fr; gap: 4px; }
}
</style>
